// Gift With Purchase System - Tag-Based Approach
// ================================================
//
// ADMIN SETUP INSTRUCTIONS:
// 1. Add tags to products that should trigger gifts
// 2. Tag format: "gwp-gift-{variant_id}"
//    Example: "gwp-gift-123456789" (where 123456789 is the gift variant ID)
// 3. Only products with subscription selling plans will trigger gifts
// 4. Each product can have one gift tag
// 5. If multiple qualifying products have different gifts, the first one found will be used
//
// HOW IT WORKS:
// - Monitors all cart changes via fetch/XHR patching
// - When cart changes, checks for qualifying subscription products
// - Fetches product data to check for gift tags
// - Adds/removes gifts based on qualifying products
// - Works with any cart system (ReCharge, Rebuy, etc.)

(function() {
  'use strict';

  // Configuration
  const config = {
    giftProperties: {
      '_gwp_source': 'gwp_system',
      '_gwp_type': 'welcome_kit',
      '_gwp_discount_title': 'Free Gift',
      '_gwp_discount_type': 'percentage',
      '_gwp_discount_value': '100'
    },
    settings: { 
      debugMode: false,  // Disabled for production
      enabled: true,
      debounceMs: 200,
      postWriteDelayMs: 200
    }
  };

  // Cache for product data to avoid repeated API calls
  const productCache = new Map();
  
  // Flag to prevent multiple simultaneous writes
  let isWriting = false;
  
  // Flag to prevent infinite loops from our own cart calls
  let isOurCartCall = false;
  
  // Circuit breaker to prevent infinite loops
  let isCircuitBreakerOpen = false;
  let circuitBreakerResetTime = 0;
  
  // Monitoring and performance tracking
  let cartCallCount = 0;
  let giftSystemCalls = 0;
  let productApiCalls = 0;
  let lastCartState = null;
  let lastCartItemCount = 0;
  let lastCartTotal = 0;
  let performanceStartTime = Date.now();

  // Track qualifying variants (variantId → quantity) from the previous processGiftLogic run.
  // null = uninitialized (first run). A variant is "new" if its ID wasn't present before
  // OR its quantity increased — both mean the user just added another qualifying unit.
  let lastKnownQualifyingVariants = null;

  // Track which gift variant IDs were actually in the cart on the previous run.
  // Used to distinguish "user removed the gift" from "a prior add failed silently".
  // null = uninitialized.
  let lastKnownGiftVariants = null;

  // sessionStorage key for gifts the user has explicitly removed
  const DISMISSED_GIFTS_KEY = 'gwp_dismissed';

  function getDismissedGifts() {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(DISMISSED_GIFTS_KEY) || '[]'));
    } catch (e) {
      return new Set();
    }
  }

  function saveDismissedGifts(set) {
    try {
      sessionStorage.setItem(DISMISSED_GIFTS_KEY, JSON.stringify([...set]));
    } catch (e) {}
  }

  function addDismissedGift(giftVariantId) {
    const dismissed = getDismissedGifts();
    dismissed.add(String(giftVariantId));
    saveDismissedGifts(dismissed);
  }

  function clearDismissedGift(giftVariantId) {
    const dismissed = getDismissedGifts();
    dismissed.delete(String(giftVariantId));
    saveDismissedGifts(dismissed);
  }
  
  // Debounce utility
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Sleep utility
  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Check if cart has meaningful changes (items added/removed)
  function hasCartChanged(newCart) {
    if (!lastCartState) {
      // First time - store initial state
      lastCartState = {
        itemCount: newCart.item_count,
        total: newCart.total_price,
        items: newCart.items.map(item => ({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties,
          selling_plan: item.selling_plan_allocation?.selling_plan?.id || null
        }))
      };
      return false; // Don't trigger on first load
    }

    const currentState = {
      itemCount: newCart.item_count,
      total: newCart.total_price,
      items: newCart.items.map(item => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties,
        selling_plan: item.selling_plan_allocation?.selling_plan?.id || null
      }))
    };

    // Check if item count changed
    if (currentState.itemCount !== lastCartState.itemCount) {
      lastCartState = currentState;
      return true;
    }

    // Check if any item quantities changed
    for (const currentItem of currentState.items) {
      const lastItem = lastCartState.items.find(item => item.id === currentItem.id);
      if (!lastItem || lastItem.quantity !== currentItem.quantity) {
        lastCartState = currentState;
        return true;
      }
    }

    // Check if any items were removed
    for (const lastItem of lastCartState.items) {
      const currentItem = currentState.items.find(item => item.id === lastItem.id);
      if (!currentItem) {
        lastCartState = currentState;
        return true;
      }
    }

    // Check if selling plans changed (subscription <-> one-time)
    for (const currentItem of currentState.items) {
      const lastItem = lastCartState.items.find(item => item.id === currentItem.id);
      if (lastItem && lastItem.selling_plan !== currentItem.selling_plan) {
        lastCartState = currentState;
        return true;
      }
    }

    return false;
  }

  // Add gift to cart
  async function addGiftToCart(giftVariantId) {
    if (isWriting) {
      return;
    }

    try {
      isWriting = true;
      giftSystemCalls++;
      
      const giftData = {
        id: giftVariantId,
        quantity: 1,
        properties: config.giftProperties
      };
      
      // Set flag to prevent infinite loop
      isOurCartCall = true;
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(giftData)
      });
      
      // Clear flag
      isOurCartCall = false;
      
      if (response.ok) {
        // Wait for cart to update
        await sleep(config.settings.postWriteDelayMs);
      } else {
        const errorData = await response.json();
        if (errorData.status === 'too_many_requests') {
          isCircuitBreakerOpen = true;
          circuitBreakerResetTime = Date.now() + (10 * 60 * 1000); // 10 minutes
        }
      }
    } catch (error) {
      isOurCartCall = false;
    } finally {
      isWriting = false;
    }
  }

  // Update cart count in header
  function updateCartCount() {
    const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count], .header__cart-count');
    
    cartCountElements.forEach(element => {
      if (element) {
        // Use originalFetch to avoid triggering the cart:updated interceptor
        window.originalFetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);
            element.textContent = totalItems;
            
            // Also update any aria-label attributes
            const parent = element.closest('a, button');
            if (parent) {
              parent.setAttribute('aria-label', `Cart with ${totalItems} items`);
            }
          })
          .catch(() => {
          });
      }
    });
  }

  // Remove gift from cart (optionally remove specific gift by variant ID)
  async function removeGiftFromCart(specificGiftVariantId = null) {
    if (isWriting) {
      return;
    }

    try {
      isWriting = true;
      giftSystemCalls++;

      // Prevent our own fetch calls from triggering the cart:updated listener
      isOurCartCall = true;
      const cartResponse = await fetch('/cart.js');
      isOurCartCall = false;

      const cart = await cartResponse.json();

      let giftItems = cart.items.filter(item =>
        item.properties &&
        item.properties._gwp_source === 'gwp_system'
      );

      // If specific gift ID provided, filter to only that gift
      if (specificGiftVariantId) {
        giftItems = giftItems.filter(item => String(item.variant_id) === String(specificGiftVariantId));
      }

      for (const gift of giftItems) {
        isOurCartCall = true;
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: gift.key,
            quantity: 0
          })
        });
        isOurCartCall = false;

        if (!response.ok) {
        }
      }

      // Wait for cart to update
      await sleep(config.settings.postWriteDelayMs);

    } catch (error) {
      isOurCartCall = false;
    } finally {
      isWriting = false;
    }
  }

  // Get gift variant ID from product tags
  // Format: gwp-{variantId}-{giftId} - Variant-specific gift ONLY
  // Each variant must have its own tag or no gift is added
  async function getGiftVariantIdFromProduct(productHandle, variantId = null) {
    // Create cache key (include variant ID if provided)
    const cacheKey = variantId ? `${productHandle}:${variantId}` : productHandle;
    
    // Check cache first
    if (productCache.has(cacheKey)) {
      return productCache.get(cacheKey);
    }

    try {
      productApiCalls++;
      
      // Fetch the product directly by handle
      const response = await fetch(`/products/${productHandle}.js`);
      
      if (!response.ok) {
        productCache.set(cacheKey, null);
        return null;
      }
      
      const product = await response.json();
      
      // Find all gwp tags (exclude legacy gwp-gift- tags)
      const gwpTags = product.tags.filter(tag => {
        if (!tag.startsWith('gwp-')) return false;
        const parts = tag.split('-');
        // Explicitly exclude legacy format: gwp-gift-{id}
        if (parts.length === 3 && parts[1] === 'gift') return false;
        return true;
      });
      
      if (gwpTags.length === 0) {
        productCache.set(cacheKey, null);
        return null;
      }
      
      let giftVariantId = null;
      
      // ONLY check for variant-specific tag (strict mode)
      if (variantId) {
        const variantTag = gwpTags.find(tag => {
          const parts = tag.split('-');
          // Format: gwp-{variantId}-{giftId}
          // Parts: [gwp, variantId, giftId]
          // Check if second part is a number matching the variant ID
          return parts.length === 3 && 
                 parts[0] === 'gwp' && 
                 parts[1] === String(variantId) &&
                 !isNaN(parts[1]);
        });
        
        if (variantTag) {
          const parts = variantTag.split('-');
          giftVariantId = parts[2]; // The gift variant ID
          productCache.set(cacheKey, giftVariantId);
          return giftVariantId;
        }
      }
      
      // No matching variant-specific tag found - do NOT add gift
      productCache.set(cacheKey, null);
      return null;
      
    } catch (error) {
      // Don't cache on network errors — let the next call retry
      return null;
    }
  }

  // Track last cart state to avoid unnecessary processing
  let lastCartSignature = null;
  
  // Create a simple signature of cart state
  function getCartSignature(cart) {
    const qualifyingIds = cart.items
      .filter(item => {
        if (item.properties && item.properties._gwp_source === 'gwp_system') return false;
        return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      })
      .map(item => `${item.variant_id}:${item.quantity}`)
      .sort()
      .join('|');
    
    const giftIds = cart.items
      .filter(item => item.properties && item.properties._gwp_source === 'gwp_system')
      .map(item => item.variant_id)
      .sort()
      .join('|');
    
    return `q:${qualifyingIds}|g:${giftIds}`;
  }

  // Main gift logic - uses cart data from event
  async function processGiftLogic(cartData = null) {
    // Check circuit breaker
    if (isCircuitBreakerOpen) {
      if (Date.now() < circuitBreakerResetTime) {
        return;
      } else {
        // Reset circuit breaker
        isCircuitBreakerOpen = false;
      }
    }
    
    if (isWriting) {
      return;
    }

    try {
      // Use provided cart data or fetch if not available
      let cart;
      if (cartData) {
        cart = cartData;
      } else {
        // Fallback: use originalFetch to bypass the cart:updated interceptor
        const cartResponse = await window.originalFetch('/cart.js');
        cart = await cartResponse.json();
      }
      
      // Check if cart state has actually changed
      const currentSignature = getCartSignature(cart);
      if (currentSignature === lastCartSignature) {
        return;
      }
      lastCartSignature = currentSignature;

      // Update cart count for any cart change
      updateCartCount();

      // Find qualifying products (subscriptions, not gifts)
      const qualifyingProducts = cart.items.filter(item => {
        // Skip gift items
        if (item.properties && item.properties._gwp_source === 'gwp_system') {
          return false;
        }

        // Check if this item has a subscription
        return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      });

      // Check if gift is in cart
      const hasGift = cart.items.some(item =>
        item.properties &&
        item.properties._gwp_source === 'gwp_system'
      );

      // If no qualifying products, remove any existing gifts
      if (qualifyingProducts.length === 0) {
        if (hasGift) {
          await removeGiftFromCart();
        }
        // All qualifying products removed — reset state so gifts are offered fresh
        // if the user adds a qualifying product again later in this session.
        lastKnownQualifyingVariants = new Map();
        lastKnownGiftVariants = null;
        try { sessionStorage.removeItem(DISMISSED_GIFTS_KEY); } catch (e) {}
        return;
      }

      // ── Detect which qualifying variants are genuinely new ──────────────────
      // Map of variantId → quantity for the current run
      const currentQualifyingVariants = new Map(
        qualifyingProducts.map(item => [String(item.variant_id), item.quantity])
      );

      const isFirstRun = lastKnownQualifyingVariants === null;

      // Two distinct cases with different dismissed-gift behavior:
      // – brandNewVariantIds: qualifying product added for the first time this session.
      //   Triggers its gift for non-dismissed gifts, but does NOT bypass a dismissed gift
      //   (e.g. product C shares the same gift as dismissed product A → gift stays dismissed).
      // – increasedVariantIds: qualifying product the user already had, now with higher qty.
      //   Bypasses the dismissed check — the user added MORE of the same qualifying item so
      //   the gift should be re-offered.
      let brandNewVariantIds = new Set();
      let increasedVariantIds = new Set();

      if (isFirstRun) {
        lastKnownQualifyingVariants = currentQualifyingVariants;
      } else {
        for (const [id, qty] of currentQualifyingVariants) {
          const prevQty = lastKnownQualifyingVariants.get(id);
          if (prevQty === undefined) {
            brandNewVariantIds.add(id);
          } else if (qty > prevQty) {
            increasedVariantIds.add(id);
          }
        }
        lastKnownQualifyingVariants = currentQualifyingVariants;
      }

      // Group qualifying products by handle to check each product's variants
      const productMap = new Map();
      qualifyingProducts.forEach(item => {
        if (!productMap.has(item.handle)) {
          productMap.set(item.handle, []);
        }
        productMap.get(item.handle).push({
          variantId: item.variant_id,
          quantity: item.quantity
        });
      });

      // Collect ALL gifts that should be in cart (supports multiple gifts)
      const expectedGifts = new Set();
      const giftsFromNewVariants = new Set();      // triggered by brand-new qualifying products
      const giftsFromIncreasedVariants = new Set(); // triggered by increased-qty qualifying products

      for (const [productHandle, variants] of productMap) {
        for (const variant of variants) {
          const foundGiftId = await getGiftVariantIdFromProduct(productHandle, variant.variantId);
          if (foundGiftId) {
            expectedGifts.add(foundGiftId);
            if (brandNewVariantIds.has(String(variant.variantId))) {
              giftsFromNewVariants.add(foundGiftId);
            }
            if (increasedVariantIds.has(String(variant.variantId))) {
              giftsFromIncreasedVariants.add(foundGiftId);
            }
          }
        }
      }

      const dismissedGifts = getDismissedGifts();

      // Get current gifts in cart
      const currentGifts = cart.items
        .filter(item => item.properties && item.properties._gwp_source === 'gwp_system')
        .map(item => String(item.variant_id));

      // Snapshot previous gift state then update for next run.
      // We only mark a gift as dismissed when we have positive evidence it was in
      // the cart last run and is now gone — this prevents a silent add failure from
      // permanently suppressing the gift.
      const prevKnownGiftVariants = lastKnownGiftVariants;
      lastKnownGiftVariants = new Set(currentGifts);

      // Find gifts to remove (in cart but not expected)
      const giftsToRemove = currentGifts.filter(giftId => !expectedGifts.has(giftId));

      // Find gifts to add:
      // – already in cart → skip
      // – triggered by a qty-increase on an existing qualifying product → always add
      //   (user added more of the same item, so dismissed state is overridden)
      // – dismissed → skip (even if a brand-new qualifying product triggers the same gift ID)
      // – otherwise add if first page load or triggered by a brand-new qualifying product
      const giftsToAdd = Array.from(expectedGifts).filter(giftId => {
        if (currentGifts.includes(giftId)) return false;
        // Qty-increase bypass: only when the gift was NOT in cart on the previous run.
        // If it was in cart before and is now gone, the user just removed it — don't re-add.
        if (giftsFromIncreasedVariants.has(giftId) &&
            (prevKnownGiftVariants === null || !prevKnownGiftVariants.has(String(giftId)))) return true;
        if (dismissedGifts.has(String(giftId))) return false;    // dismissed → always skip
        return isFirstRun || giftsFromNewVariants.has(giftId);
      });

      // Detect gifts the user manually removed.
      // Requires the gift to have been in the cart on the PREVIOUS run — otherwise a
      // silent add failure would immediately mark the gift as dismissed.
      for (const giftId of expectedGifts) {
        if (
          !currentGifts.includes(giftId) &&
          !giftsToAdd.includes(giftId) &&
          !dismissedGifts.has(String(giftId)) &&
          prevKnownGiftVariants !== null &&
          prevKnownGiftVariants.has(String(giftId))
        ) {
          addDismissedGift(giftId);
        }
      }

      // Remove unwanted gifts
      if (giftsToRemove.length > 0) {
        for (const giftId of giftsToRemove) {
          await removeGiftFromCart(giftId);
        }
      }

      // Add missing gifts
      if (giftsToAdd.length > 0) {
        for (const giftId of giftsToAdd) {
          await addGiftToCart(giftId);
          // Optimistically record the gift as seen-in-cart so that if the user
          // removes it before the next processGiftLogic run fires a real cart read,
          // prevKnownGiftVariants will correctly show the gift was present and the
          // dismiss-detection logic will mark it dismissed rather than re-adding it.
          lastKnownGiftVariants.add(String(giftId));
        }
      }
      
    } catch (error) {
    }
  }

  // Debounced gift logic
  const debouncedGiftLogic = debounce((cartData) => {
    processGiftLogic(cartData);
  }, config.settings.debounceMs);

  // Setup cart change detection
  function setupCartChangeDetection() {
    // Store original functions if not already stored
    if (!window.originalFetch) {
      window.originalFetch = window.fetch;
    }
    if (!window.originalXMLHttpRequestOpen) {
      window.originalXMLHttpRequestOpen = XMLHttpRequest.prototype.open;
    }
    
    // Patch fetch
    const originalFetch = window.originalFetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      // Check if this is a cart API call
      if (typeof args[0] === 'string' && args[0].includes('/cart')) {
        // Skip if this is our own cart call to prevent infinite loop
        if (isOurCartCall) {
          return response;
        }
        
        try {
          cartCallCount++;
          
          // Get caller info for monitoring
          const stack = new Error().stack;
          const stackLines = stack.split('\n');
          const caller = stackLines[2] || 'unknown';
          
          // Check if this is from a known problematic source
          const isFromElevar = caller.includes('dl-app-embed-block.js');
          const isFromShopEvents = caller.includes('shop_events_listener');
          const isFromPrequal = caller.includes('prequal-widget');
          const isFromOnward = caller.includes('onward.js');
          const isFromGiftSystem = caller.includes('gift-with-purchase.js');
          const isFromRecharge = caller.includes('recharge') || caller.includes('ReCharge');
          
          // Log only problematic sources
          if (isFromElevar || isFromShopEvents || isFromPrequal || isFromOnward) {
            // External app cart call detected
          }
          
          const cloned = response.clone();
          const cart = await cloned.json();
          
          // Check if this is a rate limit response
          if (cart.status === 'too_many_requests') {
            isCircuitBreakerOpen = true;
            circuitBreakerResetTime = Date.now() + (10 * 60 * 1000); // 10 minutes
            return response;
          }
          
          // Only dispatch event if cart actually changed
          if (hasCartChanged(cart)) {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
          }
        } catch (error) {
          // Ignore errors (might be non-JSON response)
        }
      }
      
      return response;
    };

    // Patch XMLHttpRequest for older cart systems
    const originalOpen = window.originalXMLHttpRequestOpen;
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this.addEventListener('load', () => {
        if (isOurCartCall) return;
        if (typeof url === 'string' && url.includes('/cart')) {
          try {
            const cart = JSON.parse(this.responseText);

            // Only dispatch event if cart actually changed
            if (hasCartChanged(cart)) {
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
            }
          } catch (error) {
            // Ignore errors (might be non-JSON response)
          }
        }
      });
      return originalOpen.call(this, method, url, ...rest);
    };
  }

  // Setup cart event listener
  function setupCartEventListener() {
    document.addEventListener('cart:updated', (e) => {
      // Pass the cart data from the event to avoid another API call
      debouncedGiftLogic(e.detail);
    });
    
    // Listen for ReCharge specific events
    document.addEventListener('recharge:cart:updated', (e) => {
      debouncedGiftLogic(e.detail);
    });
    
    // Listen for any custom cart events that might be dispatched by ReCharge
    document.addEventListener('cart:change', (e) => {
      debouncedGiftLogic(e.detail);
    });
    
    // Listen for ReCharge subscription changes
    document.addEventListener('recharge:subscription:changed', (e) => {
      // Force a cart check after a short delay to allow ReCharge to update
      setTimeout(() => {
        debouncedGiftLogic();
      }, 1000);
    });
  }

  // Initialize the gift system
  function initGiftSystem() {
    if (!config.settings.enabled) {
      return;
    }

    setupCartChangeDetection();
    setupCartEventListener();

    // Initial check on page load
    setTimeout(() => {
      debouncedGiftLogic();
      // Update cart count on page load
      updateCartCount();
    }, 1000);
  }

  // Start the system when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGiftSystem);
  } else {
    initGiftSystem();
  }

})();